// src/scripts/sqlApiHelper/staffDetailsApi.js
// ✅ Firestore staff details (venue-scoped)
// Collection: venues/{venueId}/staff_details/{staffId}

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import { db, auth, app } from "@/firebase";

function clean(v) {
  return String(v ?? "").trim();
}

function lower(v) {
  return clean(v).toLowerCase();
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Existing callable in functions/index.js.
 *
 * Deletes matching staff_login_codes docs for:
 * - venueId
 * - staffId
 */
async function deleteLinkedStaffLoginCode(venueId, staffId) {
  const vId = clean(venueId);
  const sId = clean(staffId);

  if (!vId || !sId) {
    return { ok: true, deleted: 0, skipped: true };
  }

  const functions = getFunctions(app, "us-central1");
  const callable = httpsCallable(functions, "deleteStaffLoginCode");

  const res = await callable({
    venueId: vId,
    staffId: sId,
  });

  return res.data || { ok: true, deleted: 0 };
}

/**
 * Upsert staff detail.
 * - If payload.id exists -> setDoc merge (safe even if doc doesn't exist yet)
 * - If payload.id missing -> addDoc (new id)
 *
 * payload.uid is the staff member's Firebase Auth UID — written when known
 * (e.g. after they first log in via magic link). The admin app can also
 * pre-populate it if the UID is available.
 *
 * Returns: { id }
 */
export async function upsertStaffDetail(venueId, payload) {
  const vId = clean(venueId);
  if (!vId) throw new Error("Missing venueId");

  const id = clean(payload?.id);

  const data = {
    staff_name: clean(payload?.staff_name),
    email: lower(payload?.email),
    job_title: clean(payload?.job_title),
    pay_rate: num(payload?.pay_rate),
    active: payload?.active !== false,

    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || null,
  };

  // ✅ Write the staff member's own Auth UID if provided.
  // This is the key that lets the rota portal match shift.staffId -> staff_details.
  const staffUid = clean(payload?.uid);
  if (staffUid) data.uid = staffUid;

  if (!data.staff_name) throw new Error("Missing staff_name");
  if (!data.job_title) throw new Error("Missing job_title");

  // ✅ Existing (or caller-provided) id: safe upsert
  if (id) {
    const ref = doc(db, "venues", vId, "staff_details", id);
    await setDoc(ref, data, { merge: true });
    return { id };
  }

  // ✅ New staff: addDoc generates id
  const colRef = collection(db, "venues", vId, "staff_details");
  const newRef = await addDoc(colRef, {
    ...data,
    createdAt: serverTimestamp(),
    createdBy: auth.currentUser?.uid || null,
  });

  return { id: newRef.id };
}

/**
 * Backfill a staff member's Auth UID onto their staff_details doc.
 *
 * Called from the staff-app after a successful magic-link login.
 * Finds the staff_details doc by email and writes uid + staffName onto it
 * so the rota portal can resolve names from shifts.
 *
 * Also writes staffId + staffName onto rota_access_by_email so that
 * "My Shifts" filtering works automatically.
 *
 * Returns: { staffDocId, staffName, staffRole } or null if not found.
 */
export async function backfillStaffUidAfterLogin(venueId, user) {
  if (!user?.uid || !user?.email) return null;

  const vId = clean(venueId);
  const uid = user.uid;
  const email = lower(user.email);

  if (!vId || !email) return null;

  let staffDocId = null;
  let staffName = "";
  let staffRole = "";

  // ── Find staff_details doc by email ───────────────────────────────────────
  try {
    const staffRef = collection(db, "venues", vId, "staff_details");
    const qs = await getDocs(query(staffRef, where("email", "==", email)));

    if (!qs.empty) {
      const snap = qs.docs[0];
      staffDocId = snap.id;
      const d = snap.data() || {};
      staffName = clean(d.staff_name || "");
      staffRole = clean(d.job_title || "");

      // Only write if uid isn't already correct (avoid unnecessary writes)
      if (d.uid !== uid) {
        await updateDoc(doc(db, "venues", vId, "staff_details", staffDocId), {
          uid,
          updatedAt: serverTimestamp(),
        });
      }
    } else {
      console.warn("[staffApi] no staff_details doc found for email:", email);
    }
  } catch (e) {
    console.warn("[staffApi] backfillStaffUid staff_details step failed:", e.message);
  }

  // ── Also write staffId + staffName onto rota_access_by_email ──────────────
  // This makes "My Shifts" filtering work without any manual admin config.
  try {
    const { doc: fsDoc, updateDoc: fsUpdate } = await import("firebase/firestore");
    const accessRef = fsDoc(db, "rota_access_by_email", email);

    const accessUpdates = { staffId: uid };
    if (staffName) accessUpdates.staffName = staffName;

    await fsUpdate(accessRef, accessUpdates);
  } catch (e) {
    // Doc may not exist yet or permissions may block — non-fatal
    console.warn("[staffApi] backfill rota_access_by_email step failed:", e.message);
  }

  return staffDocId ? { staffDocId, staffName, staffRole } : null;
}

/**
 * List staff for venue, ordered by name.
 */
export async function listStaffDetails(venueId) {
  const vId = clean(venueId);
  if (!vId) throw new Error("Missing venueId");

  const colRef = collection(db, "venues", vId, "staff_details");
  const snap = await getDocs(query(colRef, orderBy("staff_name", "asc")));

  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

/**
 * Delete a staff member.
 *
 * IMPORTANT:
 * This also deletes the matching staff_login_codes document by using the
 * existing deleteStaffLoginCode callable from functions/index.js.
 */
export async function deleteStaffDetail(venueId, staffId) {
  const vId = clean(venueId);
  const sId = clean(staffId);

  if (!vId) throw new Error("Missing venueId");
  if (!sId) throw new Error("Missing staffId");

  // 1) Delete the linked staff login code first.
  // If this fails, stop the delete so you do not leave an orphaned login code.
  await deleteLinkedStaffLoginCode(vId, sId);

  // 2) Delete the staff_details record.
  await deleteDoc(doc(db, "venues", vId, "staff_details", sId));

  return { ok: true };
}